import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { keepPreviousData, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import type { User } from '../../features/users/types/users.types';
import type { Order } from '../../features/orders/types/orders.types';
import type { ApprovalRequest } from '../../features/approvals/types/approvals.types';
import type { ReturnVerification, ReturnSettlement } from '../../features/receipts/types/receipts.types';
import { UserRole } from '../../lib/contracts';
import { bottleReturnsApi } from '../../features/bottleReturns/api/bottleReturns.api';
import { ordersApi } from '../../features/orders/api/orders.api';
import { normalizeOrders } from '../../lib/order.util';
import { productsApi } from '../../features/products/api/products.api';
import { approvalsApi } from '../../features/approvals/api/approvals.api';
import { adminApi } from '../../features/admin/api/admin.api';
import { getSocket } from '../../lib/socket';
import { useSessionHooks } from '../../features/auth/hooks/useSession.hook';
import { useCartHooks } from '../../features/cart/hooks/useCart.hook';
import { useOrderHooks } from '../../features/orders/hooks/useOrder.hook';
import { useReceiptHooks } from '../../features/receipts/hooks/useReceipt.hook';
import { useSystemSettings } from '../../features/system/hooks/useSystemSettings';
import { useProductsCore } from '../../features/products/hooks/useProductsCore';
import type { Product } from '../../features/products/types/products.types';
import { useUsersCore } from '../../features/users/hooks/useUsersCore';
import { readStoredCart } from '../../lib/cartStorage';
import { useCartPersistence } from '../../features/cart/hooks/useCartPersistence';
import { useOrderRealtime } from '../../features/orders/hooks/useOrderRealtime';
import { usePendingReferralClaim } from '../../features/referrals/hooks/usePendingReferralClaim';

type Toast = { id: string; message: string; type: 'info' | 'success' | 'warning' | 'error' };
type OrdersPageData = { orders: Order[]; total: number };

const ORDERS_PAGE_SIZE = 200;

/**
 * Thin app-level composition root for cross-feature orchestration.
 */
export type NinpoCoreContextValue = ReturnType<typeof useNinpoCore>;

export const useNinpoCore = () => {
  const queryClient = useQueryClient();
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [isBootstrapping, setIsBootstrapping] = useState(true);

  const [cart, setCart] = useState<{ id: string; productId?: string; quantity: number; temperaturePreference?: 'chilled' | 'ambient' | 'cold' | 'either' }[]>(() => readStoredCart());

  const isAdmin = currentUser?.role === UserRole.ADMIN || currentUser?.role === UserRole.OWNER;
  const isDriver = currentUser?.role === UserRole.DRIVER;
  const canSyncRemoteSettings =
    currentUser?.role === UserRole.OWNER || currentUser?.role === UserRole.ADMIN;

  const addToast = useCallback((message: string, type: Toast['type'] = 'info') => {
    const id = Math.random().toString(36).substring(2, 9);
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 4000);
  }, []);

  const setProducts = useCallback((newProducts: Product[] | ((prev: Product[]) => Product[])) => {
    if (typeof newProducts === 'function') {
      const prev = queryClient.getQueryData<Product[]>(['products']) || [];
      queryClient.setQueryData(['products'], newProducts(prev));
    } else {
      queryClient.setQueryData(['products'], newProducts);
    }
  }, [queryClient]);

  const [ordersPageCount, setOrdersPageCount] = useState(1);
  useEffect(() => {
    setOrdersPageCount(1);
  }, [currentUser?.id]);
  const ordersQueryKey = useMemo(
    () => ['orders', currentUser?.id, ordersPageCount] as const,
    [currentUser?.id, ordersPageCount]
  );

  const setOrders = useCallback((newOrders: Order[] | ((prev: Order[]) => Order[])) => {
    queryClient.setQueryData<OrdersPageData>(ordersQueryKey, (prev) => {
      const prevOrders = prev?.orders ?? [];
      const nextOrders = typeof newOrders === 'function' ? newOrders(prevOrders) : newOrders;
      return { orders: nextOrders, total: Math.max(prev?.total ?? 0, nextOrders.length) };
    });
  }, [queryClient, ordersQueryKey]);

  const {
    data: ordersPageData,
    refetch: fetchOrders,
    isError: ordersLoadFailed,
    error: ordersLoadError,
    isFetching: ordersFetching,
  } = useQuery({
    queryKey: ordersQueryKey,
    queryFn: async (): Promise<OrdersPageData> => {

      const pages = await Promise.all(
        Array.from({ length: ordersPageCount }, (_, pageIndex) =>
          ordersApi.listPage({ limit: ORDERS_PAGE_SIZE, skip: pageIndex * ORDERS_PAGE_SIZE })
        )
      );
      const merged = normalizeOrders(pages.flatMap((page) => page.orders));
      return { orders: merged, total: Math.max(pages[0]?.total ?? 0, merged.length) };
    },
    enabled: !!currentUser,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    placeholderData: keepPreviousData,
  });
  const orders = useMemo(() => ordersPageData?.orders ?? [], [ordersPageData]);
  const ordersTotal = ordersPageData?.total ?? 0;
  const hasMoreOrders = orders.length < ordersTotal;
  const loadMoreOrders = useCallback(() => {
    setOrdersPageCount((count) => count + 1);
  }, []);
  const invalidateOrders = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['orders'] });
  }, [queryClient]);
  const ordersErrorMessage = ordersLoadFailed
    ? (ordersLoadError instanceof Error
        ? ordersLoadError.message
        : 'Unable to load orders. Check your connection and try again.')
    : null;

  const {
    data: products = [],
    refetch: fetchProducts,
    isError: productsLoadFailed,
    error: productsLoadError,
    isFetching: productsFetching,
  } = useQuery({
    queryKey: ['products'],
    queryFn: async () => {
      const data = await productsApi.listLive();
      if (Array.isArray(data)) return data as Product[];
      if (data && typeof data === 'object') {
        const candidate = data as { products?: Product[]; data?: Product[] };
        return (candidate.products || candidate.data || []) as Product[];
      }
      return [] as Product[];
    },
    staleTime: 60_000,
  });
  const productsErrorMessage = productsLoadFailed
    ? (productsLoadError instanceof Error
        ? productsLoadError.message
        : 'Unable to load the catalog. Check your connection and try again.')
    : null;

  const {
    settings,
    setSettings,
    fetchSettings,
    syncWithBackend,
    isBackendOnline
  } = useSystemSettings(addToast, {
    canSyncRemote: canSyncRemoteSettings
  });

  const {
    users,
    setUsers,
    userStats,
    fetchUsers,
    fetchUserStats,
    updateUserProfile,
    deleteUser,
    redeemPoints
  } = useUsersCore(addToast, currentUser, setCurrentUser);

  const { applyLoginSession, restoreSession, refreshSessionUser, logout, sendPasswordResetLink } = useSessionHooks({
    setCurrentUser,
    addToast,
    currentUser
  });

  const { updateOrder, getCheckoutPreview, createOrder } = useOrderHooks({
    addToast,
    setOrders,
    onOrdersChanged: invalidateOrders
  });

  const { createProduct, updateProduct, deleteProduct } = useProductsCore(addToast);

  usePendingReferralClaim(currentUser?.id, addToast);

  const { syncCartToServer, clearCart, loadCartFromServer, applyServerCartSnapshot, getCartVersion } = useCartHooks({ currentUser, setCart });

  const isInitialSessionRestoreRef = useRef(true);

  useEffect(() => {
    let mounted = true;
    const restorePromise = Promise.race([
      restoreSession(),
      new Promise<boolean>((resolve) => setTimeout(() => resolve(false), 8000))
    ]);

    void restorePromise.finally(() => {
      if (mounted) {
        setIsBootstrapping(false);
        isInitialSessionRestoreRef.current = false;
      }
    });
    return () => {
      mounted = false;
    };
  }, [restoreSession]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const oauthStatus = params.get('oauth');
    if (!oauthStatus) return;

    if (oauthStatus === 'signed-in') {
      addToast('Signed in with Google successfully!', 'success');
      void restoreSession();
    } else if (oauthStatus === 'error') {
      addToast('Google Sign-In failed or was cancelled.', 'error');
    } else if (oauthStatus === 'expired') {
      addToast('Google Sign-In session expired. Please try again.', 'warning');
    } else if (oauthStatus === 'verify') {
      addToast('Please verify your email address to sign in.', 'warning');
    } else if (oauthStatus === 'locked') {
      addToast('Account temporarily locked. Please try again later.', 'error');
    }

    params.delete('oauth');
    const remaining = params.toString();
    const cleanUrl = `${window.location.pathname}${remaining ? `?${remaining}` : ''}${window.location.hash}`;
    window.history.replaceState({}, document.title, cleanUrl);
  }, [addToast, restoreSession]);

  /**
   * When a signed-in customer comes back (login or session restore), we want
   * the local cart to mirror their server cart. Without this, an empty local
   * state from a previous session would persist even though the server still
   * holds their items, making it look like the cart was cleared.
   *
   * Conversely, when a user logs out, drop the in-memory cart so a different
   * user signing in on the same device cannot see prior items between the
   * `setCurrentUser(null)` call and the next `loadCartFromServer` round-trip.
   * The server cart is intentionally untouched here.
   */
  const previousUserIdRef = useRef<string | null>(null);
  useEffect(() => {
    const nextUserId = currentUser?.id ?? currentUser?._id ?? null;
    if (nextUserId && nextUserId !== previousUserIdRef.current) {
      const isInitialRestore = isInitialSessionRestoreRef.current;
      isInitialSessionRestoreRef.current = false;
      const wasGuest = !previousUserIdRef.current && !isInitialRestore;
      const shouldMergeGuestCart = wasGuest && readStoredCart().length > 0;
      previousUserIdRef.current = nextUserId;
      void loadCartFromServer({ mergeGuestCart: shouldMergeGuestCart });
    } else if (!nextUserId && previousUserIdRef.current) {
      isInitialSessionRestoreRef.current = false;
      previousUserIdRef.current = null;
      // Keep local cart for guest continuity after logout (server cart untouched).
    }
  }, [currentUser, loadCartFromServer]);

  const {
    fetchReturnVerifications: rawFetchReturnVerifications,
    fetchReturnSettlements: rawFetchReturnSettlements,
    settleReturnVerification: rawSettleReturnVerification
  } = useReceiptHooks({ addToast });

  const settleReturnMutation = useMutation({
    mutationFn: async (params: {
      id: string;
      finalAcceptedCount: number;
      creditAmount: number;
      cashAmount: number;
    }) => {
      await rawSettleReturnVerification(params.id, params.finalAcceptedCount, params.creditAmount, params.cashAmount);
    },
    onMutate: async ({ id }) => {
      await queryClient.cancelQueries({ queryKey: ['returnVerifications'] });
      const previousReturns = queryClient.getQueryData<ReturnVerification[]>(['returnVerifications']);
      queryClient.setQueryData<ReturnVerification[]>(['returnVerifications'], (old) => {
        if (!old) return [];
        return old.filter((r) => r.id !== id);
      });
      return { previousReturns };
    },
    onError: (err, variables, context) => {
      if (context?.previousReturns) {
        queryClient.setQueryData(['returnVerifications'], context.previousReturns);
      }
      addToast(err instanceof Error ? err.message : 'Failed to settle return', 'error');
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['returnVerifications'] });
      void queryClient.invalidateQueries({ queryKey: ['returnSettlements'] });
    },
    onSuccess: () => {
      addToast('Return successfully settled', 'success');
    }
  });

  const settleReturnVerification = useCallback(
    async (id: string, finalAcceptedCount: number, creditAmount: number, cashAmount: number) => {
      await settleReturnMutation.mutateAsync({ id, finalAcceptedCount, creditAmount, cashAmount });
    },
    [settleReturnMutation]
   );

  const { data: returnVerifications = [], refetch: refetchReturnVerifications } = useQuery({
    queryKey: ['returnVerifications'],
    queryFn: rawFetchReturnVerifications,
    enabled: !!currentUser && isAdmin,
    staleTime: 30_000,
  });

  const { data: returnSettlements = [], refetch: refetchReturnSettlements } = useQuery({
    queryKey: ['returnSettlements'],
    queryFn: rawFetchReturnSettlements,
    enabled: !!currentUser && isAdmin,
    staleTime: 30_000,
  });

  const fetchReturnVerifications = useCallback(async () => {
    const res = await refetchReturnVerifications();
    return res.data || [];
  }, [refetchReturnVerifications]);

  const fetchReturnSettlements = useCallback(async () => {
    const res = await refetchReturnSettlements();
    return res.data || [];
  }, [refetchReturnSettlements]);

// Driver logic is native-only (mobile/android/driver)
  // useDriverHooks({ currentUser, orders });

  // Unified approvals query for Admin
  const {
    data: rawApprovals = [],
    refetch: refetchApprovals,
    isPending: isApprovalsPending,
    isFetching: isApprovalsFetching,
    isError: isApprovalsError,
    error: approvalsQueryError
  } = useQuery({
    queryKey: ['approvals', 'list', 'pending'],
    queryFn: async () => {
      const page = await approvalsApi.list({ status: 'pending', limit: 100, page: 1 });
      return page.approvals;
    },
    enabled: !!currentUser && isAdmin,
    staleTime: 30_000,
  });

  const approvals = rawApprovals;
  const isApprovalsLoading = isApprovalsPending || (isApprovalsFetching && approvals.length === 0);
  const approvalsError = isApprovalsError
    ? (approvalsQueryError instanceof Error ? approvalsQueryError.message : 'Failed to load approvals')
    : null;

  const fetchApprovals = useCallback(async () => {
    try {
      const res = await refetchApprovals();
      return res.data || [];
    } catch (e: unknown) {
      addToast(e instanceof Error ? e.message : 'Approvals feed offline', 'warning');
      return [];
    }
  }, [refetchApprovals, addToast]);

  const reviewApprovalMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: 'approved' | 'rejected' }) => {
      const result = await adminApi.reviewApproval(id, status);
      const body = result && typeof result === 'object'
        ? result as { success?: boolean; data?: { status?: unknown; type?: unknown } }
        : {};
      if (body.success !== true) {
        throw new Error('Approval review was not confirmed by the server.');
      }
      const confirmed = String(body.data?.status || '').trim().toLowerCase();
      if (confirmed !== status) {
        throw new Error(
          `Approval review was not confirmed (expected ${status}, got ${confirmed || 'unknown'}).`
        );
      }
      return {
        id,
        status: confirmed as 'approved' | 'rejected',
        type: String(body.data?.type || '').trim().toLowerCase()
      };
    },
    onError: (err) => {
      const message = err instanceof Error ? err.message : 'Failed to review approval';
      addToast(message, 'error');
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['approvals'] });
    },
    onSuccess: (data) => {
      if (data.type === 'wallet_cashout' && data.status === 'approved') {
        addToast(
          'Wallet debited. Complete bank/check payout separately — approval is not a paid transfer.',
          'info'
        );
        return;
      }
      addToast(`Approval successfully ${data.status}`, 'success');
    }
  });

  const reviewApproval = useCallback(
    async (id: string, status: 'approved' | 'rejected') => {
      await reviewApprovalMutation.mutateAsync({ id, status });
    },
    [reviewApprovalMutation]
  );

  const refreshDashboardData = useCallback(async () => {
    if (isAdmin) return;
    queryClient.invalidateQueries({ queryKey: ['orders'] });
    queryClient.invalidateQueries({ queryKey: ['products'] });
    queryClient.invalidateQueries({ queryKey: ['approvals'] });
    queryClient.invalidateQueries({ queryKey: ['returnVerifications'] });
    queryClient.invalidateQueries({ queryKey: ['returnSettlements'] });
    setLastSyncTime(new Date());
  }, [queryClient, isAdmin]);

  /**
   * D6 (hard launch v4): replace the blanket 30s session poll with a
   * server-pushed `cart`/`order` socket event + TanStack Query background
   * cache management.
   */
  useEffect(() => {
    if (!currentUser) return;
    setLastSyncTime(new Date());

    // Jittered poll is now handled by useQuery's staleTime/gcTime
    // and manual invalidation via socket events.
  }, [currentUser]);


  useEffect(() => {
    void fetchSettings();
  }, [fetchSettings, canSyncRemoteSettings]);

  useCartPersistence({
    cart,
    currentUserId: currentUser?.id ?? currentUser?._id ?? null,
    syncCartToServer
  });

  useOrderRealtime<Product>({
    currentUserId: currentUser?.id ?? currentUser?._id ?? null,
    orders,
    setOrders,
    setCart,
    setProducts,
    setLastSyncTime,
    onCartSnapshot: applyServerCartSnapshot
  });
  /**
   * D1 (hard launch v4): stabilize the `core` context value.
   *
   * Pre-v4 the hook returned a fresh object literal on every render,
   * which meant every `CoreProvider` descendant re-rendered any time
   * any state inside `useNinpoCore` changed (cart, orders, products,
   * etc). At hard-launch volume that fan-out is the dominant cost in
   * the React profiler.
   *
   * The fix: wrap the returned object in `useMemo` keyed off the
   * actual values that consumers can observe. Stable callbacks are
   * already memoized inside the sub-hooks; we only need to re-create
   * the outer object when one of the underlying state references
   * changes. Children that depend on a single field (e.g. the cart
   * count badge) will still re-render when that field changes; what
   * we eliminate is "every consumer re-renders because the wrapper
   * object identity changed for no reason."
   */
  return useMemo(
    () => ({
      currentUser,
      setCurrentUser,
      users,
      isAdmin,
      isDriver,
      setUsers,
      userStats,
      settings,
      setSettings,
      products,
      setProducts,
      productsErrorMessage,
      productsFetching,
      productsLoadFailed,
      orders,
      ordersErrorMessage,
      ordersFetching,
      ordersLoadFailed,
      setOrders,
      approvals,
      isApprovalsLoading,
      approvalsError,
      returnVerifications,
      returnSettlements,
      cart,
      setCart,
      toasts,
      addToast,
      clearCart,
      getCartVersion,
      updateOrder,
      fetchUsers,
      fetchUserStats,
      updateUserProfile,
      deleteUser,
      sendPasswordResetLink,
      redeemPoints,
      isBackendOnline,
      syncWithBackend,
      isBootstrapping,
      lastSyncTime,
      refreshDashboardData,
      applyLoginSession,
      restoreSession,
      refreshSessionUser,
      logout,
      fetchProducts,
      fetchSettings,
      createProduct,
      updateProduct,
      deleteProduct,
      fetchOrders,
      ordersTotal,
      hasMoreOrders,
      loadMoreOrders,
      invalidateOrders,
      fetchApprovals,
      fetchReturnVerifications,
      fetchReturnSettlements,
      settleReturnVerification,
      reviewApproval,
      getCheckoutPreview,
      createOrder
    }),
    // The deps below intentionally exclude functions that we already
    // know are stable (`useCallback` inside sub-hooks); they only
    // include the *state* values whose change should cause a new
    // context object to be published.
    [
      currentUser,
      users,
      isAdmin,
      isDriver,
      userStats,
      settings,
      products,
      productsErrorMessage,
      productsFetching,
      productsLoadFailed,
      orders,
      ordersErrorMessage,
      ordersFetching,
      ordersLoadFailed,
      approvals,
      isApprovalsLoading,
      approvalsError,
      returnVerifications,
      returnSettlements,
      cart,
      toasts,
      isBackendOnline,
      isBootstrapping,
      lastSyncTime,
      // Functions referenced as values (not invoked here) — listed so
      // exhaustive-deps stays happy without being meaningful churn,
      // since useCallback already keeps them stable.
      addToast,
      clearCart,
      getCartVersion,
      updateOrder,
      fetchUsers,
      fetchUserStats,
      updateUserProfile,
      deleteUser,
      sendPasswordResetLink,
      redeemPoints,
      syncWithBackend,
      refreshDashboardData,
      applyLoginSession,
      restoreSession,
      refreshSessionUser,
      logout,
      fetchProducts,
      fetchSettings,
      createProduct,
      updateProduct,
      deleteProduct,
      fetchOrders,
      ordersTotal,
      hasMoreOrders,
      loadMoreOrders,
      invalidateOrders,
      fetchApprovals,
      fetchReturnVerifications,
      fetchReturnSettlements,
      settleReturnVerification,
      reviewApproval,
      getCheckoutPreview,
      createOrder,
      setCurrentUser,
      setUsers,
      setSettings,
      setProducts,
      setOrders,
      setCart
    ]
  );
};

